// CreateVariantForm.tsx
import React from "react"
import { Form, Input, Select } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLanguage } from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { Project } from "stores/projectStore"
import { Variant } from "types/questionnaire.types"

/**
 * Interface for variant form values
 */
interface VariantFormValues {
  name: string
  languages: string[]
}

/**
 * Props interface for the CreateVariantForm component
 */
interface CreateVariantFormProps {
  /** The project data */
  project: Project
  /** Callback function to execute after variant creation */
  createVariantCallback: (updatedVariants: Variant[]) => Promise<void>
  /** Function to set the modal visibility state */
  setIsModalVisible: (visible: boolean) => void
  /** Function to insert text into the editor */
  insertVariantText: (text: string) => void
  /** The list of existing variants */
  variants: Variant[]
  /** Function to set the list of variants */
  setVariants: (variants: Variant[]) => void
  /** Function to set the selected variant */
  setSelectedVariant: (variant: Variant) => void
  /** Whether collaborative mode is enabled (skip inserting variant text) */
  collaborativeMode?: boolean
}

/**
 * A form component for creating a new variant.
 *
 * @component
 * @param props - The props for the component
 * @returns The rendered component
 */
const CreateVariantForm: React.FC<CreateVariantFormProps> = ({
  project,
  createVariantCallback,
  setIsModalVisible,
  insertVariantText,
  collaborativeMode = false,
  variants,
  setVariants,
  setSelectedVariant,
}) => {
  const [form] = Form.useForm<VariantFormValues>()

  /**
   * Submits the form and creates a new project variant.
   *
   * @async
   * @param variantFormValues - The values from the form fields
   * @returns A promise that resolves when the variant is created
   */
  const submitVariantForm = async (
    variantFormValues: VariantFormValues
  ): Promise<void> => {
    try {
      const defineVariantLine = `#DEFINE VARIANT ${variantFormValues.name}`
      const languagesLine = `#LANGUAGE ${variantFormValues.languages.join(
        ", "
      )}`

      if (
        variants !== undefined &&
        variants.some(
          (variant: Variant) => variant.name === variantFormValues.name
        )
      ) {
        notify.error(
          "Variant Already Exists",
          "A variant with this name already exists. Please choose a different name."
        )
      } else {
        // In collaborative mode, don't insert variant definition into editor
        // The variant metadata sync handles awareness across browsers
        // and saveQuestionnaire will add the definition when saving to backend
        if (!collaborativeMode) {
          // Ensure that the new variant block starts and ends with proper newlines
          const newVariantContent = `\n\n${defineVariantLine}\n${languagesLine}\n#LINE 0\n\n`
          insertVariantText(newVariantContent)
        }

        const newVariant: Variant = {
          ...variantFormValues,
          content: "",
          languages: variantFormValues.languages,
        }

        const updatedVariants: Variant[] = [...variants, newVariant]
        setVariants(updatedVariants)
        setSelectedVariant(newVariant) // Switch to the newly created variant
        setIsModalVisible(false)
        await createVariantCallback(updatedVariants)
      }
    } catch (error) {
      console.error("create variant error", error)
      throw error
    }
  }

  /**
   * Resets the form fields and hides the modal.
   *
   * @function hideModal
   */
  const hideModal = (): void => {
    form.resetFields()
    setIsModalVisible(false)
  }

  return (
    <Form
      form={form}
      name="create-project-form"
      onFinish={submitVariantForm}
      style={{ textAlign: "left" }}
      layout="vertical"
      initialValues={{
        languages:
          typeof project.Languages === "string"
            ? JSON.parse(project.Languages)
            : project.Languages,
      }}
    >
      <Form.Item
        style={{ marginBottom: "2em" }}
        name="name"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please Enter Variant Name",
          },
        ]}
      >
        <Input
          prefix={
            <FontAwesomeIcon
              className="flex-shrink-0 h-5 mr-2"
              icon={faLanguage}
            />
          }
          placeholder="Variant Name"
        />
      </Form.Item>
      <Form.Item
        name="languages"
        label="Languages"
        style={{ marginBottom: "2em" }}
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please select at least one language for this variant",
          },
        ]}
      >
        <Select
          options={project.Languages.map((language: string) => ({
            label: language,
            value: language,
          }))}
          mode="tags"
          style={{
            width: "100%",
          }}
        />
      </Form.Item>

      <div className="flex flex-row mt-5">
        <div className="ml-auto mr-2 space-x-2">
          <DataQButton type="primary" htmlType="submit">
            Create Variant
          </DataQButton>
          <DataQButton onClick={hideModal} type="secondary">
            Cancel
          </DataQButton>
        </div>
      </div>
    </Form>
  )
}

export default CreateVariantForm
